import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, EyeOff, Search } from "react-feather";
import { ChevronDownIcon } from "../../atoms/ChevronDownIcon";
import { CategoryTabStrip } from "../CategoryTabStrip";
import type { CategoryOption } from "../CategoryTabStrip/CategoryTabStrip.types";
import { rawColors } from "../../../design-system/tokens/colors";
import type { ControlSizeKey } from "../../../design-system/tokens/controlSizes";
import type { PortalMenuRect } from "../../../hooks/usePortalMenu";
import { isNearScrollEnd } from "./dropdownScroll";
import { useDropdownInternalState } from "./useDropdownInternalState";
import {
  Wrap,
  Trigger,
  TriggerLeft,
  TriggerLabel,
  Menu,
  MenuItem,
  MenuItemLeft,
  MenuItemLabel,
  MenuItemBody,
  MenuItemSmall,
  MenuItemNote,
  MenuItemMeta,
  TriggerMeta,
  TriggerRow,
  TriggerAside,
  GroupHeader,
  SearchRow,
  SearchField,
  SearchInput,
  EmptyState,
  TabsRow,
  type DropdownVariant,
} from "./Dropdown.styles";

export type { CategoryOption as DropdownTab };

export type { DropdownVariant };

export interface DropdownOption<V extends string> {
  value: V;
  label: string;
  icon?: React.ReactNode;
  /** Shown greyed and ignores clicks, e.g. a filter field with no editor yet. */
  disabled?: boolean;
  /** Second line under the label, e.g. what the field holds. */
  description?: string;
  /** Warning line with an alert icon, e.g. "High count: 126 copies". */
  warning?: string;
  /** Why the option can't be chosen. Shown on the row; a row with a reason is also disabled. */
  disabledReason?: string;
  /**
   * Optional group heading. Options render in the order given; a heading shows wherever the
   * group changes from the row before, so the caller keeps each group's options together.
   */
  group?: string;
  /**
   * Optional quiet text at the row's end (e.g. "5 values"), shown while the row is hovered or focused
   * (or always, with the Dropdown's `metaVisibility="always"`).
   */
  meta?: string;
}

export interface DropdownProps<V extends string> {
  value: V | null;
  options: DropdownOption<V>[];
  /**
   * Open state, refs and rect are optional as a group: omit all of them and the Dropdown
   * manages itself (`useDropdownInternalState`); pass them and the caller stays in control.
   */
  open?: boolean;
  onToggle?: () => void;
  onSelect: (value: V) => void;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  menuRef?: React.RefObject<HTMLUListElement>;
  menuRect?: PortalMenuRect | null;
  /** Small text before the chevron on the trigger, e.g. "186 values". */
  meta?: string;
  /** When option `meta` shows: `"hover"` (default) while the row is hovered, focused or selected; `"always"` on every row. */
  metaVisibility?: "hover" | "always";
  /** Note rendered beside the trigger, e.g. "Data model · Accounts". */
  aside?: React.ReactNode;
  /** Key handling inside the open menu (arrows, Enter, Escape). Options get `data-key` and `tabIndex={-1}` so they can take focus. */
  onMenuKeyDown?: (event: React.KeyboardEvent<HTMLUListElement>) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  size?: ControlSizeKey;
  /** When true, uses caption/3 typography on trigger and menu items (body/3 by default). */
  compact?: boolean;
  /**
   * Trigger + menu visual style.
   * - `"default"` — compact caption trigger; flat sticky search row in menu.
   * - `"ghost"` — borderless inline trigger.
   * - `"inline"` — caption-sized embed inside composite fields (e.g. member search bar).
   * - `"dashed"` — body-sized trigger with a dashed border, for an empty mapping slot.
   * - `"picker"` — body-sized trigger; panel menu with bordered search field
   *   and plain list rows (no checkmark). Use with `searchable` for ontology/metrics pickers.
   *   Pass `tabs`/`activeTabKey`/`onTabChange` to add a category tab strip above the
   *   search field — e.g. the drill-down field picker's "Hierarchy" / "Columns" tabs.
   */
  variant?: DropdownVariant;
  ariaLabel?: string;
  /** Id of the element that describes the trigger (a hint or a check result), set as `aria-describedby`. */
  ariaDescribedBy?: string;
  /** When true, renders a search input inside the menu; options are filtered by `query`. */
  searchable?: boolean;
  query?: string;
  onQueryChange?: (q: string) => void;
  searchPlaceholder?: string;
  /** When false, trigger sizes to content instead of stretching to 100% width. */
  fullWidth?: boolean;
  /** With a custom `trigger`: the wrapper drops its 160px floor and hugs the trigger (a picker inside a sentence). */
  hugTrigger?: boolean;
  /** Chevron icon size in pixels (default 16). */
  chevronSize?: number;
  /** Minimum width (px) for the portal menu — prevents clipping when trigger is narrower than options. */
  minMenuWidth?: number;
  /** Show the native vertical scrollbar when the option list overflows. */
  showScrollbar?: boolean;
  /**
   * Renders this instead of the built-in styled `Trigger` button — for callers
   * that already have their own trigger element elsewhere (e.g. a pill button
   * that also does other things). The caller still owns `triggerRef`/`onToggle`
   * on that element; Dropdown only renders the portal menu in this mode.
   * Mirrors `ActionMenu`'s `trigger` prop. Pass `null` explicitly to render no
   * trigger at all; omit the prop to keep the default built-in trigger.
   */
  trigger?: React.ReactNode;
  /**
   * When false, `options` are rendered as given — the caller (i.e. the
   * backend, via `query`) has already filtered them. Defaults to true
   * (client-side label match), which suits small static option sets only.
   */
  filterOptions?: boolean;
  /** First page in flight — shows `loadingLabel` instead of the options. */
  isLoading?: boolean;
  loadingLabel?: string;
  /** Request failed — this text (the backend's reason) replaces the options. */
  errorMessage?: string | null;
  /** Shown when there are no options to render. Defaults to "No results". */
  emptyLabel?: string;
  /** More rows exist server-side; scrolling near the end calls `onLoadMore`. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** A later page is in flight — shows a footer row and pauses `onLoadMore`. */
  isLoadingMore?: boolean;
  /**
   * Tab strip rendered above the search row — e.g. "Hierarchy" / "Columns"
   * for the drill-down field picker. Requires `activeTabKey` and
   * `onTabChange`; omit all three to render no tabs.
   */
  tabs?: CategoryOption[];
  activeTabKey?: string;
  onTabChange?: (key: string) => void;
}

export function Dropdown<V extends string>({
  value,
  options,
  open: openProp,
  onToggle: onToggleProp,
  onSelect,
  triggerRef: triggerRefProp,
  menuRef: menuRefProp,
  menuRect: menuRectProp,
  meta,
  aside,
  onMenuKeyDown,
  placeholder = "Select",
  invalid = false,
  disabled = false,
  size = "md",
  compact = false,
  variant = "default",
  ariaLabel,
  ariaDescribedBy,
  searchable = false,
  query = "",
  onQueryChange,
  searchPlaceholder = "Search",
  fullWidth = true,
  hugTrigger = false,
  chevronSize = 16,
  minMenuWidth,
  showScrollbar = false,
  metaVisibility = "hover",
  trigger,
  filterOptions = true,
  isLoading = false,
  loadingLabel = "Loading…",
  errorMessage = null,
  emptyLabel = "No results",
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  tabs,
  activeTabKey,
  onTabChange,
}: DropdownProps<V>) {
  const selfManaged = openProp === undefined && onToggleProp === undefined;
  const internal = useDropdownInternalState(selfManaged);
  const open = selfManaged ? internal.isOpen : !!openProp;
  const onToggle = onToggleProp ?? internal.onToggle;
  const triggerRef = triggerRefProp ?? internal.triggerRef;
  const menuRef = menuRefProp ?? internal.menuRef;
  const menuRect = menuRectProp !== undefined ? menuRectProp : internal.rect;
  const handleSelect = (v: V) => {
    onSelect(v);
    if (selfManaged) internal.onClose();
  };
  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const visibleOptions =
    searchable && filterOptions && q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
  // One status row replaces the options: error wins, then first-page
  // loading, then "no results" (only where the list can legitimately be
  // empty — a searchable or remote list).
  const statusText =
    errorMessage ??
    (isLoading
      ? loadingLabel
      : (searchable || !filterOptions) && visibleOptions.length === 0
        ? emptyLabel
        : null);

  const triggerNode =
    trigger !== undefined ? (
      trigger
    ) : (
        <Trigger
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          $open={open}
          $invalid={invalid}
          $size={size}
          $compact={compact}
          $variant={variant}
          $fullWidth={fullWidth}
          onClick={onToggle}
        >
          <TriggerLeft>
            {selected?.icon}
            <TriggerLabel $isPlaceholder={!selected} $variant={variant}>
              {selected?.label ?? placeholder}
            </TriggerLabel>
          </TriggerLeft>
          {(meta ?? selected?.meta) && <TriggerMeta>{meta ?? selected?.meta}</TriggerMeta>}
          <ChevronDownIcon size={chevronSize} />
        </Trigger>
    );

  return (
    <Wrap $fullWidth={fullWidth} $variant={variant} $hug={hugTrigger}>
      {aside ? (
        <TriggerRow>
          {triggerNode}
          <TriggerAside>{aside}</TriggerAside>
        </TriggerRow>
      ) : (
        triggerNode
      )}
      {open &&
        menuRect &&
        createPortal(
          <Menu
            ref={menuRef}
            role="listbox"
            $variant={variant}
            $showScrollbar={showScrollbar}
            onKeyDown={onMenuKeyDown}
            onScroll={
              onLoadMore
                ? (e) => {
                    if (hasMore && !isLoadingMore && isNearScrollEnd(e.currentTarget)) {
                      onLoadMore();
                    }
                  }
                : undefined
            }
            style={{
              ...(menuRect.bottom != null
                ? { bottom: menuRect.bottom, top: "auto" }
                : { top: menuRect.top, bottom: "auto" }),
              left: menuRect.left,
              width:
                minMenuWidth != null
                  ? Math.max(menuRect.width, minMenuWidth)
                  : menuRect.width,
              maxHeight: menuRect.maxHeight,
            }}
          >
            {tabs && tabs.length > 0 && activeTabKey != null && onTabChange && (
              <TabsRow>
                <CategoryTabStrip options={tabs} activeKey={activeTabKey} onChange={onTabChange} />
              </TabsRow>
            )}
            {searchable && (
              <SearchRow $variant={variant}>
                {variant === "picker" ? (
                  <SearchField data-testid="dropdown-search-field">
                    <Search size={14} color={rawColors.text.mediumEmphasis} />
                    <SearchInput
                      $variant={variant}
                      autoFocus
                      value={query}
                      placeholder={searchPlaceholder}
                      onChange={(e) => onQueryChange?.(e.target.value)}
                    />
                  </SearchField>
                ) : (
                  <>
                    <Search size={14} color={rawColors.text.mediumEmphasis} />
                    <SearchInput
                      $variant={variant}
                      autoFocus
                      value={query}
                      placeholder={searchPlaceholder}
                      onChange={(e) => onQueryChange?.(e.target.value)}
                    />
                  </>
                )}
              </SearchRow>
            )}
            {statusText !== null ? (
              <EmptyState
                role={errorMessage ? "alert" : "status"}
                data-testid="dropdown-status"
              >
                {statusText}
              </EmptyState>
            ) : (
              visibleOptions.map((o, index) => {
                const active = value === o.value;
                const rich = !!(o.description || o.warning || o.disabledReason);
                const disabledRow = !!o.disabled || !!o.disabledReason;
                const showGroup = o.group != null && o.group !== visibleOptions[index - 1]?.group;
                return (
                  <React.Fragment key={o.value}>
                    {showGroup && (
                      <GroupHeader role="presentation" data-testid="dropdown-group-header">
                        {o.group}
                      </GroupHeader>
                    )}
                  <MenuItem
                    role="option"
                    aria-selected={active}
                    aria-disabled={disabledRow || undefined}
                    title={disabledRow ? o.disabledReason : undefined}
                    {...(onMenuKeyDown ? { tabIndex: -1, "data-key": o.value } : {})}
                    $active={active}
                    $rich={rich}
                    $compact={compact}
                    $variant={variant}
                    $disabled={disabledRow}
                    onClick={disabledRow ? undefined : () => handleSelect(o.value)}
                  >
                    <MenuItemLeft>
                      {o.icon}
                      {rich ? (
                        <MenuItemBody>
                          <MenuItemLabel>{o.label}</MenuItemLabel>
                          {o.description && <MenuItemSmall>{o.description}</MenuItemSmall>}
                          {o.warning && (
                            <MenuItemNote>
                              <AlertTriangle size={14} color={rawColors.accent.amber500} aria-hidden="true" />
                              {o.warning}
                            </MenuItemNote>
                          )}
                          {o.disabledReason && (
                            <MenuItemNote>
                              <EyeOff size={14} color={rawColors.text.mediumEmphasis} aria-hidden="true" />
                              {o.disabledReason}
                            </MenuItemNote>
                          )}
                        </MenuItemBody>
                      ) : (
                        <MenuItemLabel>{o.label}</MenuItemLabel>
                      )}
                    </MenuItemLeft>
                    {o.meta && <MenuItemMeta $always={metaVisibility === "always"}>{o.meta}</MenuItemMeta>}
                    {active && variant !== "picker" && (
                      <Check size={16} color={rawColors.primary[500]} />
                    )}
                  </MenuItem>
                  </React.Fragment>
                );
              })
            )}
            {isLoadingMore && statusText === null && (
              <EmptyState role="status" data-testid="dropdown-loading-more">
                {loadingLabel}
              </EmptyState>
            )}
          </Menu>,
          document.body,
        )}
    </Wrap>
  );
}

export default Dropdown;
